import cn from 'classnames';
import { forwardRef } from 'react';

import Copyable from '@/shared/components/Copyable';
import { NumberDisplay } from '@/shared/components/number-display';

import { Tooltip } from 'ui/Tooltip';

interface StatisticItemProps {
  /** Full label text */
  label: string;
  /** Raw value for copying */
  value: string;
  /** Formatted display value */
  displayValue: string;
  /** Optional token symbol (shown in tooltip if truncated) */
  symbol?: string;
  /** Use accent color for label (balance items) */
  isAccent?: boolean;
  /** Custom label color */
  labelColor?: string;
  /** Raw numeric value for NumberDisplay (prevents layout shifting) */
  rawValue?: string | number;
  /** Decimal precision for NumberDisplay */
  decimalPlaces?: number;
  /** Prefix rendered before the number (e.g. "$") */
  prefix?: string;
  /** Label font size in pixels (from JS hook) */
  labelFontSize?: number;
  /** Value font size in pixels (from JS hook) */
  valueFontSize?: number;
  /** Whether symbol truncation is needed */
  truncationNeeded?: boolean;
}

/** Truncate only the token symbol if longer than maxLength chars */
const truncateSymbol = (symbol: string, maxLength = 8): { text: string; isTruncated: boolean } => {
  if (symbol.length > maxLength) {
    return { text: symbol.slice(0, maxLength - 1), isTruncated: true };
  }
  return { text: symbol, isTruncated: false };
};

/**
 * Single statistic item with label, value, tooltip, and copy functionality.
 * Font sizes are set via inline styles from the JS-based useUniformFontSizes hook.
 */
export const StatisticItem = forwardRef<HTMLDivElement, StatisticItemProps>(
  (
    {
      label,
      value,
      displayValue,
      symbol,
      isAccent,
      labelColor,
      rawValue,
      decimalPlaces,
      prefix,
      labelFontSize,
      valueFontSize,
      truncationNeeded = false,
    },
    ref,
  ) => {
    // If symbol provided and truncation needed, truncate it and replace in label
    let displayLabel = label;
    let isTruncated = false;

    if (truncationNeeded && symbol && label.includes(symbol)) {
      const { text: truncatedSymbol, isTruncated: symbolTruncated } = truncateSymbol(symbol);
      if (symbolTruncated) {
        displayLabel = label.replace(symbol, truncatedSymbol);
        isTruncated = true;
      }
    }

    return (
      <div ref={ref} className="statistic-item">
        <Tooltip
          trigger={
            <div
              style={{
                ...(labelColor ? { color: labelColor } : undefined),
                ...(labelFontSize
                  ? { fontSize: `${labelFontSize}px`, lineHeight: `${labelFontSize + 3}px` }
                  : undefined),
              }}
              className={cn(
                'statistic-item__label',
                !labelColor &&
                  (isAccent ? 'statistic-item__label--accent' : 'statistic-item__label--muted'),
              )}
            >
              {displayLabel}
              {isTruncated && <sub className="statistic-item__truncation">...</sub>}
            </div>
          }
          content={<>{label}</>}
          options={{
            placement: 'top',
            offset: 5,
          }}
        />
        <div
          className="statistic-item__value"
          style={valueFontSize ? { fontSize: `${valueFontSize}px` } : undefined}
        >
          <Copyable
            value={value}
            label={
              rawValue != null ? (
                <NumberDisplay
                  value={rawValue}
                  decimalPlaces={decimalPlaces}
                  minDecimalPlaces={2}
                  showTooltip={false}
                  nodeLeft={prefix ? <>{prefix}</> : undefined}
                />
              ) : (
                displayValue
              )
            }
          />
        </div>
      </div>
    );
  },
);

StatisticItem.displayName = 'StatisticItem';

export default StatisticItem;
